import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, Sparkles, Send, FileText } from "lucide-react";

export default function Evaluations() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [marks, setMarks] = useState({});
  const [feedback, setFeedback] = useState({});
  const [overall, setOverall] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/evaluations");
    setQueue(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const open = (a) => {
    setActive(a);
    const m = {}, f = {};
    a.questions.filter((q) => ["short", "long", "descriptive"].includes(q.type)).forEach((q) => {
      const ev = (a.evaluations || {})[q.id];
      m[q.id] = ev?.marks ?? "";
      f[q.id] = ev?.feedback ?? "";
    });
    setMarks(m);
    setFeedback(f);
    setOverall("");
  };

  const aiEvaluate = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/attempts/${active.id}/ai-evaluate`);
      const m = { ...marks }, f = { ...feedback };
      Object.entries(data.suggestions).forEach(([qid, s]) => { m[qid] = s.marks; f[qid] = s.feedback; });
      setMarks(m); setFeedback(f);
      toast.success("AI suggestions applied — review before publishing");
    } catch {
      toast.error("AI evaluation failed");
    } finally {
      setAiLoading(false);
    }
  };

  const publish = async () => {
    setSubmitting(true);
    try {
      const evaluations = Object.keys(marks).map((qid) => ({
        question_id: qid, marks: parseFloat(marks[qid] || 0), feedback: feedback[qid] || "",
      }));
      await api.post(`/attempts/${active.id}/evaluate`, { evaluations, overall_feedback: overall });
      toast.success("Result published");
      setActive(null);
      load();
    } catch {
      toast.error("Failed to publish");
    } finally {
      setSubmitting(false);
    }
  };

  const subjectiveQs = active?.questions.filter((q) => ["short", "long", "descriptive"].includes(q.type)) || [];

  return (
    <DashboardLayout nav={adminNav} title="Evaluations">
      <div className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Evaluation Queue</h1>
        <p className="text-muted-foreground mt-1">Review subjective answers, assign marks and publish results.</p>
      </div>

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>
      ) : queue.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center">
          <ClipboardCheck className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="mt-4 text-muted-foreground">Nothing to evaluate. All submissions are processed.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {queue.map((a) => (
            <div key={a.id} className="bg-card p-5" data-testid={`eval-card-${a.id}`}>
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><FileText className="h-3.5 w-3.5" /> {a.subject}</div>
              <h3 className="mt-1 font-semibold">{a.exam_title}</h3>
              <div className="mt-2 text-sm">{a.student?.name} <span className="font-mono text-muted-foreground">· {a.student?.registration_number}</span></div>
              <div className="mt-1 text-xs text-muted-foreground">Objective: {a.objective_score} · Submitted {new Date(a.submitted_at).toLocaleDateString()}</div>
              <Button className="mt-4 w-full rounded-sm" onClick={() => open(a)} data-testid={`evaluate-${a.id}`}>Evaluate</Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{active?.exam_title} — {active?.student?.name}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-between items-center bg-secondary/50 border border-border p-3 text-sm">
            <span>Objective score: <span className="font-mono font-semibold">{active?.objective_score}</span> · Total marks: <span className="font-mono">{active?.total_marks}</span></span>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={aiEvaluate} disabled={aiLoading} data-testid="ai-evaluate-btn">
              {aiLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} AI Suggest
            </Button>
          </div>

          <div className="space-y-5 mt-2">
            {subjectiveQs.map((q, i) => {
              const ans = (active.answers || {})[q.id]?.text || "";
              return (
                <div key={q.id} className="border border-border p-4" data-testid={`eval-q-${q.id}`}>
                  <div className="flex items-center gap-2 text-xs mb-2">
                    <span className="font-mono text-muted-foreground">Q{i + 1}</span>
                    <span className="px-1.5 py-0.5 bg-accent text-accent-foreground uppercase">{q.type}</span>
                    <span className="font-mono text-muted-foreground">Max {q.marks} marks</span>
                  </div>
                  <p className="text-sm font-medium">{q.text}</p>
                  <div className="mt-2 bg-secondary/50 p-3 text-sm whitespace-pre-wrap">{ans || <span className="text-muted-foreground italic">No answer provided</span>}</div>
                  {q.expected_answer && <p className="mt-2 text-xs text-muted-foreground"><span className="font-medium">Model:</span> {q.expected_answer}</p>}
                  <div className="mt-3 grid grid-cols-[120px_1fr] gap-3">
                    <div className="space-y-1.5">
                      <Label>Marks</Label>
                      <Input type="number" step="0.5" max={q.marks} min={0} value={marks[q.id]} onChange={(e) => setMarks({ ...marks, [q.id]: e.target.value })} data-testid={`marks-${q.id}`} />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Feedback</Label>
                      <Input value={feedback[q.id]} onChange={(e) => setFeedback({ ...feedback, [q.id]: e.target.value })} placeholder="Comment for the student" />
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="space-y-1.5">
              <Label>Overall feedback / improvement suggestions</Label>
              <Textarea value={overall} onChange={(e) => setOverall(e.target.value)} placeholder="Summary feedback shown in the report" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)}>Cancel</Button>
            <Button onClick={publish} disabled={submitting} className="rounded-sm" data-testid="publish-result-btn">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Publish Result
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
